// THE WHEEL'S RADII — one definition, imported by the renderer AND the tests.
//
// This module exists because the numbers drifted THREE times. Wheel.tsx held
// them and scripts/assets/roulette_ball_test.mjs re-declared them by hand, so
// every time the wheel was re-measured the test kept passing against the OLD
// geometry — green while guarding nothing. Now there is one source and the test
// bundles this file, so a radius change either updates the assertions or fails
// them. It cannot silently pass.
//
// ── WHERE THE NUMBERS COME FROM ──────────────────────────────────────────
// Measured off docs/assets/roulette_ref/"closeup of spinnig board.jpeg" (519x490)
// by fitting the wheel's centre to (272,240) — minimising the radial spread of
// the red pocket pixels — then taking raw RGB profiles along eight rays.
//
//    px    / 232      feature
//    24.7    0.106    brass hub
//    99      0.427    cone edge, and the number band's INNER radius
//   150      0.647    the numerals
//   161      0.694    number band OUTER
//   164      0.707    gold ring
//   180      0.776    the eight brass fittings
//   194.5    0.838    the ball's track (white blob found at (427,123))
//   200      0.862    inner edge of the wooden rim
//   232      1.000    outer edge of the wood
//
// An earlier eyeballed set had every ring 11-13% too far out, split the single
// thick band into a thin band plus an invented "apron", and deleted the dark
// ball track on the mistaken belief that the ball ran on bare wood. The pixels
// say r168..r198 is near-black on every ray, with the ball inside it.

/** viewBox is 400x400; the wood's outer edge leaves a 4px margin. */
export const R_OUTER = 196;

export const RATIO = Object.freeze({
  hub: 0.106,
  cone: 0.419,
  goldIn: 0.432,      // thin gold ring, spans 0.421..0.443
  ring2In: 0.443,     // the INNER pocket ring begins
  ring2Out: 0.543,
  groove: 0.547,      // dark groove between the two rings, spans 0.543..0.551
  bandIn: 0.551,      // the OUTER pocket ring — the one carrying the numerals
  numerals: 0.623,
  bandOut: 0.688,
  gold: 0.702,
  fittings: 0.776,
  ballTrack: 0.838,
  rimIn: 0.862,
  /* WHERE THE WOOD STOPS. The reference runs its rim to the full 1.000 R — a
     13.8%-of-radius band that is the largest block of brown on the screen. The
     owner asked for a narrower rim, so the bowl is DRAWN to 0.945 and the band
     becomes 8.3% of R: a 40% narrower rim.
     Nothing inside moves. Pockets, numerals, gold rings, the ball track and
     every physics constant below keep their measured values, which is why
     scripts/assets/roulette_ball_test.mjs still holds. */
  rimOut: 0.945,
});

export const R_RIM_IN = Math.round(RATIO.rimIn * R_OUTER);      // 169
export const R_RIM_OUT = Math.round(RATIO.rimOut * R_OUTER);    // 185
export const R_FITTING = Math.round(RATIO.fittings * R_OUTER);  // 152
export const R_GOLD = Math.round(RATIO.gold * R_OUTER);         // 138
export const R_BAND_OUT = Math.round(RATIO.bandOut * R_OUTER);  // 135
export const R_NUM = Math.round(RATIO.numerals * R_OUTER);      // 122
export const R_BAND_IN = Math.round(RATIO.bandIn * R_OUTER);    // 108
export const R_GROOVE = RATIO.groove * R_OUTER;                 // 107.2
export const R_GROOVE_W = (0.551 - 0.543) * R_OUTER;            // 1.6
export const R_RING2_OUT = Math.round(RATIO.ring2Out * R_OUTER);// 106
export const R_RING2_IN = Math.round(RATIO.ring2In * R_OUTER);  // 87
export const R_GOLD_IN = RATIO.goldIn * R_OUTER;                // 84.7
export const R_GOLD_IN_W = (0.443 - 0.421) * R_OUTER;           // 4.3
export const R_CONE = Math.round(RATIO.cone * R_OUTER);         // 82
export const R_HUB = Math.round(RATIO.hub * R_OUTER);           // 21

export const R_BALL = Math.round((RATIO.ballTrack * R_OUTER) / 0.985);  // 167
export const R_TRACK = 0.985 * R_BALL;   // 164.5 — inside the dark track ring
/* WHERE THE BALL COMES TO REST.
   Was 0.78 = 130.3, which is the OUTER half of the number band — the numerals sit
   at R_NUM = 122, so the ball landed on top of them and half-covered the number it
   had just won. On a real wheel (owner's photograph) the numbers are painted on
   the ring and the ball drops into the pocket INSIDE them: you read the number,
   and the ball sits below it.
   0.69 = 115.2 puts it between R_BAND_IN (108) and R_NUM (122) — inside the
   numerals, still well clear of the cone, and the number it won stays legible
   with the ball resting under it exactly as the photograph shows. */
/* WHERE THE BALL COMES TO REST — DERIVED FROM THE POCKET RING ITSELF.
 *
 * This wheel is drawn as TWO rings of 37 wedges, which is what a real wheel is
 * and what the owner's photograph shows:
 *
 *     108 .. 135   the NUMBER ring   — the painted segments, numerals at 122
 *      87 .. 106   the POCKET ring   — the compartments the ball actually lands in
 *
 * Every earlier value put the ball somewhere in the NUMBER ring: 130.3 sat on top
 * of the numerals, and 115.2 — my "fix" — only moved it to the number ring's inner
 * wall, so it straddled the gold ring at the cone's edge. Both were wrong in the
 * same way: the ball was in the band where the numbers are painted, not in the
 * band where the pockets are. You read the number on the ring; the ball sits in
 * the pocket inside it.
 *
 * So this is no longer a tuned fraction. It is the CENTRE OF THE POCKET RING,
 * computed from the same two constants the wedges are drawn with — move the
 * artwork and the ball moves with it, which is the whole reason the last three
 * attempts each needed a fourth. */
export const R_REST = (R_RING2_IN + R_RING2_OUT) / 2;    // 96.5 — mid-pocket
/* The engine takes a radius parameter and needs this as a fraction of it. Derived,
 * never typed: engine.ts used to carry its own hand-copied 0.78 and the two
 * silently drifted, which is how a geometry change moved the winner glow but not
 * the ball. */
export const REST_FRAC = R_REST / R_BALL;
